import { isSeizureItemLog } from '@features/logs/utils/logUtils'
import { colors } from '@lmc/common/constants'
import type { Home, Resident } from '@lmc/ts-common'
import type { FC } from 'react'
import styled from 'styled-components'
import Field from '../../fields'

interface Props {
  selectedResident?: Resident
  fields: Array<Record<string, any>>
  formId: string
  item: {
    amountOneKey?: string
    amountTwoKey?: string
    id?: string
  }
  features?: Home
  testId?: string
}

const ModalTitleSelector: FC<Props> = ({ fields, formId, item, testId }) => {
  const renderMeasurementField = ({
    key,
    amountKey,
    fields,
    formId
  }: {
    key: string
    amountKey: string
    fields: Array<Record<string, any>>
    formId: string
  }) => (
    <Field
      formId={formId}
      keys={key}
      amountKey={amountKey}
      layout="column"
      type="measurement"
      positiveValuesOnly={key === 'amount1'}
      values={fields}
    />
  )

  const showMeasurements = !isSeizureItemLog(item?.id)

  return (
    <Container data-testid={testId || 'modal-title-selector'}>
      <FormField
        data-cy="title-input-form"
        data-testid={
          testId ? `${testId}-title-field` : 'modal-title-selector-title-field'
        }
      >
        <Field
          id="form-title"
          formId={formId}
          keys="title"
          label="Title"
          layout="column"
          required
          type="text"
          values={fields}
          data-testid={
            testId
              ? `${testId}-title-input`
              : 'modal-title-selector-title-input'
          }
        />
      </FormField>
      <FormField
        data-cy="description-input-form"
        data-testid={
          testId
            ? `${testId}-description-field`
            : 'modal-title-selector-description-field'
        }
      >
        <Field
          id="form-description"
          formId={formId}
          keys="description"
          label="Description"
          layout="column"
          required
          type="textarea"
          values={fields}
          data-testid={
            testId
              ? `${testId}-description-input`
              : 'modal-title-selector-description-input'
          }
        />
        <HelpText
          data-testid={
            testId ? `${testId}-help-text` : 'modal-title-selector-help-text'
          }
        >
          {HELP_TEXT}
        </HelpText>
      </FormField>
      {showMeasurements && item?.amountOneKey && (
        <FormField
          data-cy="amount-input-form"
          data-testid={
            testId
              ? `${testId}-amount1-field`
              : 'modal-title-selector-amount1-field'
          }
        >
          {renderMeasurementField({
            key: 'amount1',
            amountKey: item.amountOneKey,
            fields,
            formId
          })}
        </FormField>
      )}
      {showMeasurements && item?.amountTwoKey && (
        <FormField
          data-cy="amount-input-form"
          data-testid={
            testId
              ? `${testId}-amount2-field`
              : 'modal-title-selector-amount2-field'
          }
        >
          {renderMeasurementField({
            key: 'amount2',
            amountKey: item.amountTwoKey,
            fields,
            formId
          })}
        </FormField>
      )}
    </Container>
  )
}

const HELP_TEXT = 'This text will be visible to your carers in the Carer App'

export default ModalTitleSelector

const Container = styled.div`
  margin: auto;
  max-width: 400px;
`

const FormField = styled.div`
  max-width: 400px;
  padding-bottom: 14px;
`

const HelpText = styled.p`
  color: ${colors.darkgrey};
  font-size: 12px;
  margin-bottom: 0;
  margin-top: 4px;
`
